import type { ReactNode } from 'react';

// =============================================================================
// COLOR SYSTEM
// =============================================================================

/**
 * Color palette for quantities - using distinct, accessible colors
 */
export const QUANTITY_COLORS = [
  '#2563eb', // blue-600
  '#dc2626', // red-600
  '#16a34a', // green-600
  '#9333ea', // purple-600
  '#ea580c', // orange-600
  '#0891b2', // cyan-600
  '#c026d3', // fuchsia-600
  '#65a30d', // lime-600
];

/**
 * Separate color palette for block diagrams - chosen to avoid clashing with text colors
 */
export const DIAGRAM_COLORS = [
  '#60a5fa', // blue-400 - lighter blue
  '#f87171', // red-400 - lighter red
  '#4ade80', // green-400 - lighter green
  '#c084fc', // purple-400 - lighter purple
  '#fb923c', // orange-400 - lighter orange
  '#22d3ee', // cyan-400 - lighter cyan
  '#e879f9', // fuchsia-400 - lighter fuchsia
  '#a3e635', // lime-400 - lighter lime
];

/**
 * Helper to create colored quantity text
 */
export function ColoredQuantity({ count, label, colorIndex }: { count: number; label: string; colorIndex: number }) {
  return (
    <strong style={{ color: QUANTITY_COLORS[colorIndex % QUANTITY_COLORS.length] }}>
      {count} {label}
    </strong>
  );
}

// =============================================================================
// TYPES — with React fragments for colored text
// =============================================================================

export type RatioNotation = 'colon' | 'fraction' | 'word';
export type RatioType = 'part-to-part' | 'part-to-whole';

export interface RatioPart {
  key: string;
  singular: string;
  plural: string;
  count: number;
  colorIndex: number; // Index into QUANTITY_COLORS
}

export interface RatioContext {
  id: string;
  name: string;
  category:
    | 'human-anatomy'
    | 'animal-anatomy'
    | 'nature'
    | 'biology'
    | 'chemistry'
    | 'astronomy'
    | 'human-made'
    | 'time';
  /** Function that renders the context statement with colored quantities */
  renderContext: () => ReactNode;
  parts: RatioPart[];
  /** If defined, part-to-whole ratios are meaningful */
  whole?: RatioPart;
}

/**
 * Single unified question record.
 */
export interface RatioQuestion {
  id: string;
  contextId: string;
  type: RatioType;
  numerator: string; // key from parts or 'whole'
  denominator: string; // key from parts or 'whole'
  notation: RatioNotation;
  reverse: boolean; // whether the ASKED order is swapped in the prompt & answer
  /** only meaningful for part-to-whole questions */
  explicitTotal: boolean;
}

export interface ContextCoverageRow {
  contextId: string;
  contextName: string;
  category: RatioContext['category'];
  total: number;
  p2p: number;
  p2w: number;
  notation: Record<RatioNotation, number>;
  reverseOrder: number;
  explicitTotal: number;
  implicitTotal: number;
  equalParts: number;
}

// =============================================================================
// DATA: RATIO CONTEXTS (with colored fragments)
// =============================================================================

export const CONTEXTS: RatioContext[] = [
  // Human Anatomy
  {
    id: 'adult-teeth',
    name: 'Adult Human Teeth',
    category: 'human-anatomy',
    renderContext: () => (
      <>
        Adults usually have <ColoredQuantity count={32} label="permanent teeth" colorIndex={4} />. These include{' '}
        <ColoredQuantity count={8} label="incisors" colorIndex={0} /> (used for cutting),{' '}
        <ColoredQuantity count={4} label="canines" colorIndex={1} /> (used for tearing),{' '}
        <ColoredQuantity count={8} label="premolars" colorIndex={2} />, and{' '}
        <ColoredQuantity count={12} label="molars" colorIndex={3} /> (used for grinding).
      </>
    ),
    parts: [
      { key: 'incisors', singular: 'incisor', plural: 'incisors', count: 8, colorIndex: 0 },
      { key: 'canines', singular: 'canine', plural: 'canines', count: 4, colorIndex: 1 },
      { key: 'premolars', singular: 'premolar', plural: 'premolars', count: 8, colorIndex: 2 },
      { key: 'molars', singular: 'molar', plural: 'molars', count: 12, colorIndex: 3 },
    ],
    whole: { key: 'whole', singular: 'tooth', plural: 'teeth', count: 32, colorIndex: 4 },
  },
  {
    id: 'spine-vertebrae',
    name: 'Human Spine Vertebrae',
    category: 'human-anatomy',
    renderContext: () => (
      <>
        The human spine is made of <ColoredQuantity count={24} label="movable vertebrae" colorIndex={3} />. There are{' '}
        <ColoredQuantity count={7} label="in the neck (cervical)" colorIndex={0} />,{' '}
        <ColoredQuantity count={12} label="in the upper back (thoracic)" colorIndex={1} />, and{' '}
        <ColoredQuantity count={5} label="in the lower back (lumbar)" colorIndex={2} />.
      </>
    ),
    parts: [
      { key: 'cervical', singular: 'cervical vertebra', plural: 'cervical vertebrae', count: 7, colorIndex: 0 },
      { key: 'thoracic', singular: 'thoracic vertebra', plural: 'thoracic vertebrae', count: 12, colorIndex: 1 },
      { key: 'lumbar', singular: 'lumbar vertebra', plural: 'lumbar vertebrae', count: 5, colorIndex: 2 },
    ],
    whole: { key: 'whole', singular: 'movable vertebra', plural: 'movable vertebrae', count: 24, colorIndex: 3 },
  },
  {
    id: 'human-ribs',
    name: 'Human Ribs (One Side)',
    category: 'human-anatomy',
    renderContext: () => (
      <>
        Each side of your rib cage has <ColoredQuantity count={12} label="ribs" colorIndex={3} />. These include{' '}
        <ColoredQuantity count={7} label="true ribs" colorIndex={0} />,{' '}
        <ColoredQuantity count={3} label="false ribs" colorIndex={1} />, and{' '}
        <ColoredQuantity count={2} label="floating ribs" colorIndex={2} />.
      </>
    ),
    parts: [
      { key: 'trueRibs', singular: 'true rib', plural: 'true ribs', count: 7, colorIndex: 0 },
      { key: 'falseRibs', singular: 'false rib', plural: 'false ribs', count: 3, colorIndex: 1 },
      { key: 'floatingRibs', singular: 'floating rib', plural: 'floating ribs', count: 2, colorIndex: 2 },
    ],
    whole: { key: 'whole', singular: 'rib', plural: 'ribs', count: 12, colorIndex: 3 },
  },

  // Animal Anatomy
  {
    id: 'spider-eyes',
    name: 'Spider Eyes',
    category: 'animal-anatomy',
    renderContext: () => (
      <>
        Many spiders have <ColoredQuantity count={8} label="eyes" colorIndex={2} />. They usually have{' '}
        <ColoredQuantity count={2} label="larger main eyes" colorIndex={0} /> and{' '}
        <ColoredQuantity count={6} label="smaller secondary eyes" colorIndex={1} />.
      </>
    ),
    parts: [
      { key: 'principalEyes', singular: 'principal eye', plural: 'principal eyes', count: 2, colorIndex: 0 },
      { key: 'secondaryEyes', singular: 'secondary eye', plural: 'secondary eyes', count: 6, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'eye', plural: 'eyes', count: 8, colorIndex: 2 },
  },
  {
    id: 'starfish-arms',
    name: 'Starfish Arms',
    category: 'animal-anatomy',
    renderContext: () => (
      <>
        A typical starfish has <ColoredQuantity count={5} label="arms" colorIndex={0} /> that spread out from the center
        of its body.
      </>
    ),
    parts: [{ key: 'arms', singular: 'arm', plural: 'arms', count: 5, colorIndex: 0 }],
    whole: { key: 'whole', singular: 'starfish', plural: 'starfish', count: 1, colorIndex: 1 },
  },
  {
    id: 'octopus-anatomy',
    name: 'Octopus Anatomy',
    category: 'animal-anatomy',
    renderContext: () => (
      <>
        An octopus has <ColoredQuantity count={8} label="flexible arms" colorIndex={0} />, and it also has{' '}
        <ColoredQuantity count={3} label="hearts" colorIndex={1} /> that help pump blood through its body.
      </>
    ),
    parts: [
      { key: 'arms', singular: 'arm', plural: 'arms', count: 8, colorIndex: 0 },
      { key: 'hearts', singular: 'heart', plural: 'hearts', count: 3, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'octopus', plural: 'octopuses', count: 1, colorIndex: 2 },
  },
  {
    id: 'crab-limbs',
    name: 'Crab Limbs',
    category: 'animal-anatomy',
    renderContext: () => (
      <>
        A crab has <ColoredQuantity count={10} label="limbs" colorIndex={2} />. It uses{' '}
        <ColoredQuantity count={8} label="of them for walking" colorIndex={0} />, and the other{' '}
        <ColoredQuantity count={2} label="are claws" colorIndex={1} /> used for grabbing and defense.
      </>
    ),
    parts: [
      { key: 'walkingLegs', singular: 'walking leg', plural: 'walking legs', count: 8, colorIndex: 0 },
      { key: 'claws', singular: 'claw', plural: 'claws', count: 2, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'crab', plural: 'crabs', count: 1, colorIndex: 2 },
  },
  {
    id: 'honeybee-anatomy',
    name: 'Honeybee Anatomy',
    category: 'animal-anatomy',
    renderContext: () => (
      <>
        A honeybee has <ColoredQuantity count={6} label="legs" colorIndex={0} /> for moving around and{' '}
        <ColoredQuantity count={4} label="wings" colorIndex={1} /> that help it fly and hover near flowers.
      </>
    ),
    parts: [
      { key: 'legs', singular: 'leg', plural: 'legs', count: 6, colorIndex: 0 },
      { key: 'wings', singular: 'wing', plural: 'wings', count: 4, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'honeybee', plural: 'honeybees', count: 1, colorIndex: 2 },
  },

  // Plants / Nature
  {
    id: 'lily-petals',
    name: 'Lily Tepals',
    category: 'nature',
    renderContext: () => (
      <>
        A lily flower has <ColoredQuantity count={6} label="petal-like parts total" colorIndex={2} />. There are{' '}
        <ColoredQuantity count={3} label="outer tepals" colorIndex={0} /> and{' '}
        <ColoredQuantity count={3} label="inner tepals" colorIndex={1} /> arranged in a circular pattern.
      </>
    ),
    parts: [
      { key: 'outerTepals', singular: 'outer tepal', plural: 'outer tepals', count: 3, colorIndex: 0 },
      { key: 'innerTepals', singular: 'inner tepal', plural: 'inner tepals', count: 3, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'tepal', plural: 'tepals', count: 6, colorIndex: 2 },
  },
  {
    id: 'snowflake-structure',
    name: 'Snowflake Structure',
    category: 'nature',
    renderContext: () => (
      <>
        A snowflake usually has <ColoredQuantity count={6} label="main branches" colorIndex={0} /> that spread out from
        its center, forming a beautiful symmetrical shape.
      </>
    ),
    parts: [{ key: 'branches', singular: 'branch', plural: 'branches', count: 6, colorIndex: 0 }],
    whole: { key: 'whole', singular: 'snowflake', plural: 'snowflakes', count: 1, colorIndex: 1 },
  },

  // Chemistry
  {
    id: 'water-molecule',
    name: 'Water Molecule (H₂O)',
    category: 'chemistry',
    renderContext: () => (
      <>
        A water molecule is made of <ColoredQuantity count={3} label="atoms" colorIndex={2} />:{' '}
        <ColoredQuantity count={2} label="hydrogen atoms" colorIndex={0} /> and{' '}
        <ColoredQuantity count={1} label="oxygen atom" colorIndex={1} /> all connected together.
      </>
    ),
    parts: [
      { key: 'hydrogen', singular: 'hydrogen atom', plural: 'hydrogen atoms', count: 2, colorIndex: 0 },
      { key: 'oxygen', singular: 'oxygen atom', plural: 'oxygen atoms', count: 1, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'atom', plural: 'atoms', count: 3, colorIndex: 2 },
  },
  {
    id: 'quartz-molecule',
    name: 'Quartz (SiO₂)',
    category: 'chemistry',
    renderContext: () => (
      <>
        Quartz is made of a chemical called silicon dioxide. Each unit has{' '}
        <ColoredQuantity count={1} label="silicon atom" colorIndex={0} /> and{' '}
        <ColoredQuantity count={2} label="oxygen atoms" colorIndex={1} />.
      </>
    ),
    parts: [
      { key: 'silicon', singular: 'silicon atom', plural: 'silicon atoms', count: 1, colorIndex: 0 },
      { key: 'oxygen', singular: 'oxygen atom', plural: 'oxygen atoms', count: 2, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'atom', plural: 'atoms', count: 3, colorIndex: 2 },
  },
  {
    id: 'glucose-molecule',
    name: 'Glucose Molecule (C₆H₁₂O₆)',
    category: 'chemistry',
    renderContext: () => (
      <>
        A glucose molecule, which is a type of sugar our bodies use for energy, has{' '}
        <ColoredQuantity count={6} label="carbon atoms" colorIndex={0} />,{' '}
        <ColoredQuantity count={12} label="hydrogen atoms" colorIndex={1} />, and{' '}
        <ColoredQuantity count={6} label="oxygen atoms" colorIndex={2} />.
      </>
    ),
    parts: [
      { key: 'carbon', singular: 'carbon atom', plural: 'carbon atoms', count: 6, colorIndex: 0 },
      { key: 'hydrogen', singular: 'hydrogen atom', plural: 'hydrogen atoms', count: 12, colorIndex: 1 },
      { key: 'oxygen', singular: 'oxygen atom', plural: 'oxygen atoms', count: 6, colorIndex: 2 },
    ],
    whole: { key: 'whole', singular: 'atom', plural: 'atoms', count: 24, colorIndex: 3 },
  },
  {
    id: 'dna-base-pairing',
    name: 'DNA Base Pairing',
    category: 'biology',
    renderContext: () => (
      <>
        DNA (which tells your cells how to make your body) uses four chemical units: adenine (A), thymine (T), cytosine
        (C), and guanine (G). A and T come in pairs, so for every A, there is a matching T. (C and G also come in
        pairs.)
      </>
    ),
    parts: [
      { key: 'adenine', singular: 'adenine (A) unit', plural: 'adenine (A) units', count: 1, colorIndex: 0 },
      { key: 'thymine', singular: 'thymine (T) unit', plural: 'thymine (T) units', count: 1, colorIndex: 1 },
    ],
    // no whole; p2p only
  },

  // Astronomy
  {
    id: 'inner-outer-planets',
    name: 'Solar System Planet Groups',
    category: 'astronomy',
    renderContext: () => (
      <>
        Our solar system has <ColoredQuantity count={8} label="planets" colorIndex={2} />. The{' '}
        <ColoredQuantity count={4} label="inner planets" colorIndex={0} /> are inside the asteroid belt—Mercury, Venus,
        Earth, and Mars. The <ColoredQuantity count={4} label="outer planets" colorIndex={1} /> are outside the asteroid
        belt—Jupiter, Saturn, Uranus, and Neptune.
      </>
    ),
    parts: [
      { key: 'innerPlanets', singular: 'inner planet', plural: 'inner planets', count: 4, colorIndex: 0 },
      { key: 'outerPlanets', singular: 'outer planet', plural: 'outer planets', count: 4, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'planet', plural: 'planets', count: 8, colorIndex: 2 },
  },
  {
    id: 'rocky-gas-planets',
    name: 'Solar System Planet Types',
    category: 'astronomy',
    renderContext: () => (
      <>
        In our solar system, there are{' '}
        <ColoredQuantity count={4} label="small rocky (terrestrial) planets" colorIndex={0} />
        —Mercury, Venus, Earth, and Mars—and <ColoredQuantity count={4} label="giant planets" colorIndex={1} />
        —Jupiter and Saturn (gas giants), Uranus and Neptune (ice giants).
      </>
    ),
    parts: [
      { key: 'rockyPlanets', singular: 'rocky planet', plural: 'rocky planets', count: 4, colorIndex: 0 },
      { key: 'gasGiants', singular: 'giant planet', plural: 'giant planets', count: 4, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'planet', plural: 'planets', count: 8, colorIndex: 2 },
  },
  {
    id: 'galilean-resonance',
    name: 'Galilean Moon Orbital Resonance',
    category: 'astronomy',
    renderContext: () => (
      <>
        Jupiter&apos;s moons Io, Europa, and Ganymede orbit in perfect 4:2:1 resonance-when Ganymede completes{' '}
        <ColoredQuantity count={1} label="orbit" colorIndex={2} />, Europa completes{' '}
        <ColoredQuantity count={2} label="" colorIndex={1} />, and Io completes{' '}
        <ColoredQuantity count={4} label="" colorIndex={0} />.
      </>
    ),
    parts: [
      { key: 'ioOrbits', singular: 'Io orbit', plural: 'Io orbits', count: 4, colorIndex: 0 },
      { key: 'europaOrbits', singular: 'Europa orbit', plural: 'Europa orbits', count: 2, colorIndex: 1 },
      { key: 'ganymedeOrbits', singular: 'Ganymede orbit', plural: 'Ganymede orbits', count: 1, colorIndex: 2 },
    ],
    // no whole; p2p only
  },

  // Human-made
  {
    id: 'deck-color',
    name: 'Playing Card Colors',
    category: 'human-made',
    renderContext: () => (
      <>
        A standard deck of playing cards has <ColoredQuantity count={52} label="cards" colorIndex={2} />. Half of them
        are red, and half are black.
      </>
    ),
    parts: [
      { key: 'red', singular: 'red card', plural: 'red cards', count: 26, colorIndex: 0 },
      { key: 'black', singular: 'black card', plural: 'black cards', count: 26, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'card', plural: 'cards', count: 52, colorIndex: 2 },
  },
  {
    id: 'chess-squares',
    name: 'Chess Board Squares',
    category: 'human-made',
    renderContext: () => (
      <>
        A chessboard has <ColoredQuantity count={64} label="squares" colorIndex={2} /> arranged in 8 rows and 8 columns.
        Half the squares are light and half are dark.
      </>
    ),
    parts: [
      { key: 'light', singular: 'light square', plural: 'light squares', count: 32, colorIndex: 0 },
      { key: 'dark', singular: 'dark square', plural: 'dark squares', count: 32, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'square', plural: 'squares', count: 64, colorIndex: 2 },
  },
  {
    id: 'piano-octave',
    name: 'Piano Octave Keys',
    category: 'human-made',
    renderContext: () => (
      <>
        One octave on a piano has <ColoredQuantity count={12} label="keys" colorIndex={2} />. There are{' '}
        <ColoredQuantity count={7} label="white keys" colorIndex={0} /> and{' '}
        <ColoredQuantity count={5} label="black keys" colorIndex={1} /> that repeat in a pattern.
      </>
    ),
    parts: [
      { key: 'white', singular: 'white key', plural: 'white keys', count: 7, colorIndex: 0 },
      { key: 'black', singular: 'black key', plural: 'black keys', count: 5, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'key', plural: 'keys', count: 12, colorIndex: 2 },
  },

  // Time / Biology
  {
    id: 'week-days',
    name: 'Days in a Week',
    category: 'time',
    renderContext: () => (
      <>
        A week has <ColoredQuantity count={7} label="days" colorIndex={2} />. Most people consider{' '}
        <ColoredQuantity count={5} label="of them weekdays" colorIndex={0} /> and{' '}
        <ColoredQuantity count={2} label="of them weekend days" colorIndex={1} />.
      </>
    ),
    parts: [
      { key: 'weekdays', singular: 'weekday', plural: 'weekdays', count: 5, colorIndex: 0 },
      { key: 'weekend', singular: 'weekend day', plural: 'weekend days', count: 2, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'day', plural: 'days', count: 7, colorIndex: 2 },
  },
  {
    id: 'car-features',
    name: 'Car Tires and Mirrors',
    category: 'human-made',
    renderContext: () => (
      <>
        Most cars roll on <ColoredQuantity count={4} label="tires" colorIndex={0} /> and use{' '}
        <ColoredQuantity count={3} label="mirrors" colorIndex={1} /> (2 side mirrors and 1 rearview mirror) to help the
        driver see around the car.
      </>
    ),
    parts: [
      { key: 'tires', singular: 'tire', plural: 'tires', count: 4, colorIndex: 0 },
      { key: 'mirrors', singular: 'mirror', plural: 'mirrors', count: 3, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'car', plural: 'cars', count: 1, colorIndex: 2 },
  },
  {
    id: 'virus-capsid',
    name: 'Virus Capsid (Soccer-ball shape)',
    category: 'biology',
    renderContext: () => (
      <>
        Some viruses are built just like a soccer ball. Their shell, called a capsid, uses{' '}
        <ColoredQuantity count={12} label="pentagon pieces" colorIndex={0} /> and{' '}
        <ColoredQuantity count={20} label="hexagon pieces" colorIndex={1} /> that fit together.
      </>
    ),
    parts: [
      { key: 'pentagons', singular: 'pentagon piece', plural: 'pentagon pieces', count: 12, colorIndex: 0 },
      { key: 'hexagons', singular: 'hexagon piece', plural: 'hexagon pieces', count: 20, colorIndex: 1 },
    ],
    whole: { key: 'whole', singular: 'building block', plural: 'building blocks', count: 32, colorIndex: 2 },
  },
];

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

export function findContext(id: string): RatioContext {
  const ctx = CONTEXTS.find(c => c.id === id);
  if (!ctx) throw new Error(`Context not found: ${id}`);
  return ctx;
}

function findPart(context: RatioContext, key: string): RatioPart | undefined {
  if (key === 'whole') return context.whole;
  return context.parts.find(p => p.key === key);
}

function getPartCount(context: RatioContext, key: string): number {
  const part = findPart(context, key);
  if (!part) throw new Error(`Part ${key} not found in context ${context.id}`);
  return part.count;
}

function getPartPluralName(context: RatioContext, key: string): string {
  const part = findPart(context, key);
  if (!part) return key;
  return part.plural;
}

function getPartColorIndex(context: RatioContext, key: string): number {
  const part = findPart(context, key);
  if (!part) return 0;
  return part.colorIndex;
}

export function formatAnswer(num: number, denom: number, notation: RatioNotation): string {
  switch (notation) {
    case 'colon':
      return `${num}:${denom}`;
    case 'fraction':
      return `${num}/${denom}`;
    case 'word':
      return `${num} to ${denom}`;
  }
}

function getNotationInstruction(notation: RatioNotation): ReactNode {
  switch (notation) {
    case 'colon':
      return (
        <>
          using a <span style={{ fontWeight: 700, color: 'white' }}>colon</span>
        </>
      );
    case 'fraction':
      return (
        <>
          as a <span style={{ fontWeight: 700, color: 'white' }}>fraction</span>
        </>
      );
    case 'word':
      return (
        <>
          using the word <span style={{ fontWeight: 700, color: 'white' }}>&quot;to&quot;</span>
        </>
      );
  }
}

/**
 * Render the complete problem with colored quantities
 */
export function renderProblemContent(q: RatioQuestion): {
  context: ReactNode;
  question: ReactNode;
} {
  const ctx = findContext(q.contextId);

  const explicitWhole = q.explicitTotal && q.type === 'part-to-whole' ? ctx.whole : undefined;
  const context = (
    <>
      {ctx.renderContext()}
      {explicitWhole && (
        <span>
          {' '}
          In total, there are{' '}
          <ColoredQuantity
            count={explicitWhole.count}
            label={explicitWhole.plural}
            colorIndex={explicitWhole.colorIndex}
          />
          .
        </span>
      )}
    </>
  );

  // Order asked in the prompt
  const askedNumeratorKey = q.reverse ? q.denominator : q.numerator;
  const askedDenominatorKey = q.reverse ? q.numerator : q.denominator;

  // Get plural names and colors
  const askedNumName = getPartPluralName(ctx, askedNumeratorKey);
  const askedDenName = getPartPluralName(ctx, askedDenominatorKey);
  const numColorIndex = getPartColorIndex(ctx, askedNumeratorKey);
  const denColorIndex = getPartColorIndex(ctx, askedDenominatorKey);

  const question = (
    <>
      Write the ratio of{' '}
      <strong style={{ color: QUANTITY_COLORS[numColorIndex % QUANTITY_COLORS.length] }}>{askedNumName}</strong> to{' '}
      <strong style={{ color: QUANTITY_COLORS[denColorIndex % QUANTITY_COLORS.length] }}>{askedDenName}</strong>{' '}
      {getNotationInstruction(q.notation)}.
    </>
  );

  return {
    context,
    question,
  };
}

export function computeAnswer(q: RatioQuestion): string {
  const ctx = findContext(q.contextId);
  const numValue = getPartCount(ctx, q.numerator);
  const denValue = getPartCount(ctx, q.denominator);
  const first = q.reverse ? denValue : numValue;
  const second = q.reverse ? numValue : denValue;
  return formatAnswer(first, second, q.notation);
}

// =============================================================================
// GENERATION — all relevant questions per context
// =============================================================================

export function generateAllQuestionsForContext(ctx: RatioContext): RatioQuestion[] {
  const questions: RatioQuestion[] = [];
  const notations: RatioNotation[] = ['colon', 'fraction', 'word'];

  // Part-to-part
  for (let i = 0; i < ctx.parts.length; i++) {
    for (let j = i + 1; j < ctx.parts.length; j++) {
      const a = ctx.parts[i];
      const b = ctx.parts[j];

      for (const notation of notations) {
        // Forward (a to b)
        questions.push({
          id: `${ctx.id}-p2p-${a.key}-${b.key}-${notation}`,
          contextId: ctx.id,
          type: 'part-to-part',
          numerator: a.key,
          denominator: b.key,
          notation,
          reverse: false,
          explicitTotal: false,
        });

        // Reverse (b to a)
        questions.push({
          id: `${ctx.id}-p2p-${b.key}-${a.key}-${notation}`,
          contextId: ctx.id,
          type: 'part-to-part',
          numerator: a.key,
          denominator: b.key,
          notation,
          reverse: true,
          explicitTotal: false,
        });
      }
    }
  }

  // Part-to-whole (only if whole exists)
  if (ctx.whole) {
    for (const part of ctx.parts) {
      for (const notation of notations) {
        // Only generate explicit total questions if whole.count > 1
        // (saying "In total, there is 1 starfish" is redundant)
        if (ctx.whole.count > 1) {
          // Part to whole (explicit)
          questions.push({
            id: `${ctx.id}-p2w-${part.key}-whole-${notation}-explicit`,
            contextId: ctx.id,
            type: 'part-to-whole',
            numerator: part.key,
            denominator: 'whole',
            notation,
            reverse: false,
            explicitTotal: true,
          });

          // Whole to part (reverse, explicit)
          questions.push({
            id: `${ctx.id}-p2w-whole-${part.key}-${notation}-explicit`,
            contextId: ctx.id,
            type: 'part-to-whole',
            numerator: part.key,
            denominator: 'whole',
            notation,
            reverse: true,
            explicitTotal: true,
          });
        }

        // Always generate implicit total questions
        // Part to whole (implicit)
        questions.push({
          id: `${ctx.id}-p2w-${part.key}-whole-${notation}-implicit`,
          contextId: ctx.id,
          type: 'part-to-whole',
          numerator: part.key,
          denominator: 'whole',
          notation,
          reverse: false,
          explicitTotal: false,
        });

        // Whole to part (reverse, implicit)
        questions.push({
          id: `${ctx.id}-p2w-whole-${part.key}-${notation}-implicit`,
          contextId: ctx.id,
          type: 'part-to-whole',
          numerator: part.key,
          denominator: 'whole',
          notation,
          reverse: true,
          explicitTotal: false,
        });
      }
    }
  }

  return questions;
}
